'use client';

import type { Ref } from 'react';
import { TableCell, TableHead, TableRow } from '@/primitives';
import {
  getCategoryTint,
  EBV_TRAIT_COLUMN_HEADER_HEIGHT,
  type EbvConfigGroup,
  type EbvConfigItem,
  type EbvGroupingOptions,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import EbvGroupHeader from '@/components/publicSearch/searchSection/EbvGroupHeader';
import { EbvTraitHeaderLabelFromConfig } from '@/components/publicSearch/animal/ebv/EbvTraitHeaderLabel';
import type { EbvTraitHeaderContext } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import { getEbvCellContext } from './helpers';
import { Z_HEAD_CATEGORY, Z_HEAD_EBV, Z_HEAD_NAME } from './constants';
import type { MatingTableChrome } from './useMatingTableChrome';

interface MatingTableHeadProps {
  theadRef: Ref<HTMLTableSectionElement>;
  categoryHeaderRowRef: Ref<HTMLTableRowElement>;
  ebvGroups: EbvConfigGroup[];
  visibleEbvs: EbvConfigItem[];
  groupStartFields: Set<string>;
  chrome: MatingTableChrome;
  /** Sticky offset (px) for the trait-label row, measured from the category-group row. */
  categoryHeaderHeight: number;
  traitHeaderContext?: EbvTraitHeaderContext;
  ebvGroupingOptions?: EbvGroupingOptions;
}

/** Sticky two-row header: category group row + trait-label row (desktop mating table). */
export default function MatingTableHead({
  theadRef,
  categoryHeaderRowRef,
  ebvGroups,
  visibleEbvs,
  groupStartFields,
  chrome,
  categoryHeaderHeight,
  traitHeaderContext,
  ebvGroupingOptions,
}: MatingTableHeadProps) {
  return (
    <TableHead ref={theadRef}>
      {ebvGroups.length > 0 && (
        <TableRow ref={categoryHeaderRowRef}>
          <TableCell
            rowSpan={2}
            aria-label="Animal"
            sx={{
              fontWeight: 400,
              fontSize: '0.8125rem',
              minWidth: 200,
              maxWidth: 800,
              whiteSpace: 'nowrap',
              position: 'sticky',
              left: 0,
              top: 0,
              zIndex: Z_HEAD_NAME,
              verticalAlign: 'middle',
              color: 'text.primary',
              backgroundColor: chrome.paperSurface,
              borderBottom: chrome.headerBottom,
              boxShadow: chrome.stickyColShadow,
            }}
          />
          {ebvGroups.map((group, groupIndex) => {
            const tint = getCategoryTint(group.category, ebvGroupingOptions);
            return (
              <TableCell
                key={group.category}
                colSpan={group.items.length}
                align="center"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  px: 0.5,
                  py: 0.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: Z_HEAD_CATEGORY,
                  color: 'text.primary',
                  backgroundColor: chrome.categoryHeaderBackground(tint),
                  borderBottom: chrome.cellBorder,
                  ...(groupIndex > 0 && {
                    borderLeft: chrome.cellBorder,
                  }),
                }}
              >
                <EbvGroupHeader category={group.category} />
              </TableCell>
            );
          })}
        </TableRow>
      )}
      <TableRow>
        {ebvGroups.length === 0 && (
          <TableCell
            aria-label="Animal"
            sx={{
              fontWeight: 400,
              fontSize: '0.8125rem',
              minWidth: 200,
              maxWidth: 800,
              whiteSpace: 'nowrap',
              position: 'sticky',
              left: 0,
              top: 0,
              zIndex: Z_HEAD_NAME,
              color: 'text.primary',
              backgroundColor: chrome.paperSurface,
              borderBottom: chrome.headerBottom,
              boxShadow: chrome.stickyColShadow,
            }}
          />
        )}
        {visibleEbvs.map((ebvConfig, index) => {
          const { highlighted, prevHighlighted, tint, previousSameCategory } =
            getEbvCellContext(
              ebvConfig,
              visibleEbvs[index - 1],
              ebvGroupingOptions,
            );
          return (
            <TableCell
              key={ebvConfig.field}
              align="center"
              sx={{
                fontWeight: 400,
                whiteSpace: 'normal',
                lineHeight: 1.3,
                fontSize: '0.75rem',
                minWidth: 65,
                minHeight: EBV_TRAIT_COLUMN_HEADER_HEIGHT,
                px: 1,
                color: 'text.primary',
                backgroundColor: highlighted
                  ? chrome.ebvHeaderHighlightBg
                  : chrome.categoryHeaderBackground(tint),
                borderBottom: chrome.headerBottom,
                position: 'sticky',
                top: ebvGroups.length > 0 ? categoryHeaderHeight : 0,
                zIndex: Z_HEAD_EBV,
                ...(groupStartFields.has(ebvConfig.field) && {
                  borderLeft: chrome.cellBorder,
                }),
                ...(highlighted && {
                  borderTop: chrome.ebvHighlightBorder,
                  borderRight: chrome.ebvHighlightBorder,
                  ...(prevHighlighted
                    ? {}
                    : {
                        borderLeft: chrome.ebvHighlightBorder,
                      }),
                }),
                // Category-themed trait header (matches the grid column
                // header): tinted bg + category-coloured block border.
                ...(!highlighted && {
                  borderTop: chrome.categoryBorder(tint),
                  borderRight: chrome.categoryBorder(tint),
                  ...(previousSameCategory
                    ? {}
                    : {
                        borderLeft: chrome.categoryBorder(tint),
                      }),
                }),
              }}
            >
              <EbvTraitHeaderLabelFromConfig
                label={ebvConfig.label.replace(/\^/g, ' ')}
                config={ebvConfig}
                traitHeaderContext={traitHeaderContext}
                typographyProps={{
                  color: 'text.primary',
                  sx: { fontSize: '0.75rem', lineHeight: 1.3 },
                }}
              />
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
